import { hasLowerLevel, isMapId, levelAt, mapInfo, worldToRadar, type Level, type MapId } from "@69ly/maps";
import type { Combo, Stuff } from "@69ly/shared";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, NavLink, useNavigate, useParams, useSearchParams } from "react-router";
import { GettingStarted } from "../components/GettingStarted.tsx";
import { Icon } from "../components/Icon.tsx";
import { MapFilters } from "../components/MapFilters.tsx";
import { MapSwitch } from "../components/MapSwitch.tsx";
import { StackedMarkers, type MapItem } from "../components/MarkerStack.tsx";
import { StuffSheet } from "../components/StuffSheet.tsx";
import { OnMap, Radar, ThrowLine } from "../components/Radar.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { applyComboFilters, applyFilters, authorsOf, useFilters } from "../lib/filters.ts";
import { useCallouts } from "../lib/callouts.ts";
import { useStuffs } from "../lib/stuff-queries.ts";
import { useCombos } from "../lib/teamplay-queries.ts";
import { ComboGlyphs, ComboOnMap, ComboPanel, comboSpot, defaultComboShow, type ComboShow } from "../components/ComboBits.tsx";
import { GrenadeIcon, GrenadeMark, MarkerIcon } from "../components/MarkerIcon.tsx";

/** A single (where it lands) or a combo (where it is thrown from) on the Utility map. */
interface Marker extends MapItem {
  stuff?: Stuff;
  combo?: Combo;
}

/** Which radar a stuff shows on: where it lands (or, without a landing height, where it is thrown). */
export const stuffLevel = (map: MapId, s: Stuff): Level => levelAt(map, s.landing.z ?? s.throw.z);

export function useMapParam(): MapId | null {
  const { map } = useParams();
  return isMapId(map) ? map : null;
}

/** Utility, Executions, Post-plant: the parts of a map. */
export type MapSection = "utility" | "executions" | "postPlant" | "strats" | "demos";

export function MapSections({ map, active }: { map: MapId; active: MapSection }) {
  const { t } = useTranslation();
  const items = [
    { key: "utility", to: `/library/${map}` },
    { key: "executions", to: `/library/${map}/executions` },
    { key: "postPlant", to: `/library/${map}/post-plant` },
    { key: "strats", to: `/library/${map}/strats` },
    { key: "demos", to: `/library/${map}/demos` },
  ] as const;
  return (
    <nav className="map-sections" aria-label={t("mapView.sections")}>
      {items.map((it) => (
        <Link
          key={it.key}
          to={it.to}
          className={`map-section${it.key === active ? " is-active" : ""}`}
          aria-current={it.key === active ? "page" : undefined}
        >
          {t(`mapView.${it.key}`)}
        </Link>
      ))}
    </nav>
  );
}

/** The bar under the header: map switch, sections, then the section's own views and actions. */
export function MapBar({
  map,
  view = "map",
  count,
  section = "utility",
  actions,
  countLabel,
}: {
  map: MapId;
  view?: "map" | "list" | "learn";
  count?: number;
  /** What the count counts, e.g. "2 executions" (the Utility map counts stuffs). */
  countLabel?: string;
  section?: MapSection;
  actions?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qs = params.toString() ? `?${params.toString()}` : "";
  const path = (m: string) =>
    section === "executions"
      ? `/library/${m}/executions`
      : section === "postPlant"
        ? `/library/${m}/post-plant`
        : section === "strats"
          ? `/library/${m}/strats`
          : section === "demos"
            ? `/library/${m}/demos`
            : `/library/${m}${view === "map" ? "" : `/${view}`}`;
  return (
    <div className="map-bar">
      <MapSwitch map={map} onPick={(m) => navigate(path(m))} />
      <MapSections map={map} active={section} />
      {count !== undefined && <span className="muted small">{countLabel ?? t("library.count", { count })}</span>}
      {section === "utility" ? (
        <>
          <div className="segmented" role="group" aria-label={t("mapView.map")}>
            <NavLink to={`/library/${map}${qs}`} end className="seg">
              {t("mapView.map")}
            </NavLink>
            <NavLink to={`/library/${map}/list${qs}`} className="seg">
              {t("mapView.list")}
            </NavLink>
            <NavLink to={`/library/${map}/learn`} className="seg">
              {t("mapView.learn")}
            </NavLink>
          </div>
          <Link to={`/library/${map}/new`} className="btn btn-primary">
            <Icon name="plus" />
            {t("mapView.new")}
          </Link>
          <Link to={`/library/${map}/combos/new`} className="btn btn-secondary">
            <Icon name="plus" />
            {t("mapView.newCombo")}
          </Link>
          <Link to={`/library/${map}/import`} className="btn btn-quiet" title={t("import.button")}>
            <Icon name="copy" />
            {t("import.button")}
          </Link>
        </>
      ) : (
        <div className="map-bar-actions">{actions}</div>
      )}
    </div>
  );
}

/** "From T Spawn to Jungle": the callouts of the throw. */
export function PlacesLine({ map, stuff }: { map: string; stuff: Pick<Stuff, "throw" | "landing"> }) {
  const { t } = useTranslation();
  const places = useCallouts();
  if (!isMapId(map)) return null;
  const { from, to } = places.places(map, stuff);
  if (!from && !to) return null;
  return <p className="small places-line">{from && to ? t("places.fromTo", { from, to }) : to ? t("places.to", { to }) : t("places.from", { from })}</p>;
}

/** What the side panel shows for the selected stuff: all of it, no need to open its page. */
export function StuffPanel({ stuff }: { stuff: Stuff }) {
  return <StuffSheet stuff={stuff} />;
}

/** The Utility page: Valve's radar with every single where it lands and every combo where it is thrown from. */
export function MapViewPage() {
  const { t } = useTranslation();
  const map = useMapParam();
  const { scope } = useCurrentTeam();
  const stuffs = useStuffs(scope, map ?? "");
  const combos = useCombos(scope, map ?? "");
  const f = useFilters();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("s");
  const selectedComboId = params.get("c");
  const [level, setLevel] = useState<Level>("upper");

  const kind = f.filters.kind;
  const callouts = useCallouts();
  const shown = useMemo(() => {
    if (kind === "combos" || !map) return [];
    const placesText = (s: Stuff) => Object.values(callouts.places(map, s)).filter(Boolean).join(" ");
    return applyFilters(stuffs.data ?? [], f.filters, placesText);
  }, [stuffs.data, f.filters, kind, callouts, map]);
  const shownCombos = useMemo(
    () => (kind === "singles" ? [] : applyComboFilters(combos.data ?? [], f.filters).filter((c) => comboSpot(c))),
    [combos.data, f.filters, kind],
  );
  const selected = shown.find((s) => s.id === selectedId) ?? null;
  const selectedCombo = shownCombos.find((c) => c.id === selectedComboId) ?? null;
  // What the selected combo shows (its video or one throw), shared by the panel and the map.
  const [comboShow, setComboShow] = useState<{ id: string; show: ComboShow } | null>(null);
  const show: ComboShow | null = selectedCombo ? (comboShow?.id === selectedCombo.id ? comboShow.show : defaultComboShow(selectedCombo)) : null;
  const showCombo = (s: ComboShow) => selectedCombo && setComboShow({ id: selectedCombo.id, show: s });

  // Selecting something on the other floor of Nuke switches the radar to it.
  useEffect(() => {
    if (map && selected) setLevel(stuffLevel(map, selected));
    const spot = selectedCombo && comboSpot(selectedCombo);
    if (map && spot) setLevel(levelAt(map, spot.z));
  }, [map, selected, selectedCombo]);

  if (!map) return <Navigate to="/library" replace />;
  const info = mapInfo(map);
  if (stuffs.isPending || combos.isPending) return <Loading />;
  if (stuffs.error) return <ErrorState message={errorText(t, stuffs.error)} onRetry={() => void stuffs.refetch()} />;
  if (combos.error) return <ErrorState message={errorText(t, combos.error)} onRetry={() => void combos.refetch()} />;

  const all = stuffs.data;
  const total = all.length + combos.data.length;
  const select = (key: "s" | "c", id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("s");
        next.delete("c");
        if (id) next.set(key, id);
        return next;
      },
      { replace: true },
    );
  const onLevel = shown.filter((s) => stuffLevel(map, s) === level);
  const combosOnLevel = shownCombos.filter((c) => levelAt(map, comboSpot(c)!.z) === level);
  const nothingShown = shown.length === 0 && shownCombos.length === 0;
  // With one selected, the map shows only it: the others come back with Show all.
  const focused = !!selected || !!selectedCombo;
  const markers: Marker[] = [
    ...onLevel.filter((s) => !focused || s.id === selected?.id).map((s) => ({ key: `s${s.id}`, ...worldToRadar(map, s.landing.x, s.landing.y), stuff: s })),
    ...combosOnLevel.filter((c) => !focused || c.id === selectedCombo?.id).map((c) => {
      const spot = comboSpot(c)!;
      return { key: `c${c.id}`, ...worldToRadar(map, spot.x, spot.y), combo: c };
    }),
  ];

  return (
    <div className="library">
      <MapBar map={map} view="map" count={total} />
      <div className="library-body">
        <section className="map-frame" aria-label={info.name}>
          <Radar turnable map={map} mapName={info.name} level={level} dim={total === 0} onBackground={() => select("s", null)}>
            {selected && (
              <>
                <ThrowLine map={map} from={selected.throw} to={selected.landing} side={selected.side} />
                {levelAt(map, selected.throw.z) === level && (
                  <OnMap map={map} x={selected.throw.x} y={selected.throw.y} className="throw-spot">
                    <span className={`throw-marker side-${selected.side}`} title={t("mapView.throwFrom")}>
                      <MarkerIcon kind="launch" grenade={selected.grenade} side={selected.side} selected />
                    </span>
                  </OnMap>
                )}
              </>
            )}
            {selectedCombo && <ComboOnMap map={map} combo={selectedCombo} level={level} active={typeof show === "number" ? show : null} onStep={showCombo} />}
            <StackedMarkers
              items={markers}
              isSelected={(m) => (m.stuff ? m.stuff.id === selectedId : m.combo?.id === selectedComboId)}
              renderOrigin={(m) => (m.stuff ? <MarkerIcon kind="launch" grenade={m.stuff.grenade} side={m.stuff.side} /> : null)}
              lineClass={(m) => (m.stuff ? `side-${m.stuff.side}` : undefined)}
              origin={(m) => (m.stuff && levelAt(map, m.stuff.throw.z) === level ? { ...worldToRadar(map, m.stuff.throw.x, m.stuff.throw.y), ...m.stuff.throw } : null)}
              onSelect={(m) => (m.stuff ? select("s", m.stuff.id) : m.combo && select("c", m.combo.id))}
              label={(m) => m.stuff?.title ?? m.combo?.title ?? ""}
              detail={(m) => (m.stuff ? t(`grenadeShort.${m.stuff.grenade}`) : t("combo.kind"))}
              renderGlyph={(m) =>
                m.stuff ? <GrenadeIcon grenade={m.stuff.grenade} side={m.stuff.side} size={22} /> : m.combo ? <ComboGlyphs combo={m.combo} size={9} /> : null
              }
              renderPillGlyph={(m) => {
                const s = m.stuff ?? m.combo?.steps[0]?.stuff;
                const side = m.stuff?.side ?? m.combo?.side ?? "t";
                return s ? <GrenadeMark grenade={s.grenade} side={side} size={26} /> : null;
              }}
              kindOf={(m) => (m.stuff ? `${m.stuff.grenade}-${m.stuff.side}` : `combo-${m.combo?.steps[0]?.stuff.grenade}-${m.combo?.side}`)}
              sideOf={(m) => m.stuff?.side ?? m.combo?.side ?? "t"}
              renderMarker={(m) =>
                m.stuff ? (
                  <button
                    type="button"
                    className={`marker side-${m.stuff.side}`}
                    aria-pressed={m.stuff.id === selectedId}
                    aria-label={t("mapView.marker", { title: m.stuff.title, grenade: t(`grenade.${m.stuff.grenade}`) })}
                    title={m.stuff.title}
                    onClick={(e) => {
                      e.stopPropagation();
                      select("s", m.stuff!.id === selectedId ? null : m.stuff!.id);
                    }}
                  >
                    <MarkerIcon kind="impact" grenade={m.stuff.grenade} side={m.stuff.side} selected={m.stuff.id === selectedId} />
                  </button>
                ) : m.combo ? (
                  <button
                    type="button"
                    className={`marker combo-marker side-${m.combo.side}`}
                    aria-pressed={m.combo.id === selectedComboId}
                    aria-label={t("combo.marker", { title: m.combo.title, count: m.combo.steps.length })}
                    title={m.combo.title}
                    onClick={(e) => {
                      e.stopPropagation();
                      select("c", m.combo!.id === selectedComboId ? null : m.combo!.id);
                    }}
                  >
                    <ComboGlyphs combo={m.combo} />
                  </button>
                ) : null
              }
            />
          </Radar>
          {total > 0 && (
            <>
              <div className="map-overlay map-overlay-top">
                <MapFilters {...f} count={onLevel.length + combosOnLevel.length} glass authors={authorsOf([...all, ...combos.data])} />
              </div>
              <div className="map-overlay map-overlay-bottom glass">
                {focused && (
                  <button type="button" className="btn btn-quiet btn-small map-show-all" onClick={() => select("s", null)}>
                    <Icon name="eye" size={14} />
                    {t("mapView.showAll", { count: onLevel.length + combosOnLevel.length })}
                  </button>
                )}
                {/* What the map shows: singles where they land, combos where they are thrown from. */}
                <div className="segmented" role="group" aria-label={t("mapView.kind")}>
                  {(["singles", "combos"] as const).map((k) => (
                    <button key={k} type="button" className="seg" aria-pressed={kind === k} onClick={() => f.update({ kind: k })}>
                      {t(`mapView.kinds.${k}`)}
                    </button>
                  ))}
                </div>
                {hasLowerLevel(map) && (
                  <div className="segmented" role="group" aria-label={t("level.label")}>
                    {(["upper", "lower"] as const).map((l) => (
                      <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                        {t(`level.${l}`)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          {total === 0 && (
            <div className="glass-card empty-card">
              <h1 className="section-title">{t("library.emptyTitle", { map: info.name })}</h1>
              <p>{t("library.emptyIntro")}</p>
              <Link to={`/library/${map}/new`} className="btn btn-primary">
                <Icon name="plus" />
                {t("library.createFirst")}
              </Link>
            </div>
          )}
        </section>

        {total === 0 ? (
          <GettingStarted hasStuff={false} newStuffTo={`/library/${map}/new`} />
        ) : (
          <aside className="card panel" aria-live="polite">
            {selected ? (
              <StuffPanel stuff={selected} />
            ) : selectedCombo ? (
              <ComboPanel combo={selectedCombo} show={show ?? 0} onShow={showCombo} />
            ) : nothingShown ? (
              <div className="panel-empty">
                <p>{t("mapView.noMatch")}</p>
                <button type="button" className="btn btn-secondary" onClick={f.clear}>
                  {t("mapView.clearFilters")}
                </button>
              </div>
            ) : (
              <div className="panel-empty">
                <p className="muted">{t("mapView.selectHint")}</p>
                <ul className="mini-list">
                  {shownCombos.slice(0, 4).map((c) => (
                    <li key={c.id}>
                      <button type="button" className="mini-item" onClick={() => select("c", c.id)}>
                        <ComboGlyphs combo={c} size={10} />
                        <span>{c.title}</span>
                        <span className="muted small">{t("combo.kind")}</span>
                      </button>
                    </li>
                  ))}
                  {shown.slice(0, 8 - Math.min(4, shownCombos.length)).map((s) => (
                    <li key={s.id}>
                      <button type="button" className="mini-item" onClick={() => select("s", s.id)}>
                        <GrenadeIcon grenade={s.grenade} side={s.side} size={24} />
                        <span>{s.title}</span>
                        <span className="muted small">{t(`areaShort.${s.area}`)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
